import {
  Autocomplete,
  Box,
  MenuItem,
  TextField,
  type AutocompleteRenderInputParams,
  type SxProps,
  type Theme,
} from "@mui/material";
import { isNil } from "lodash";
import { useCallback } from "react";
import { BenchIntegrationType } from "../../../generated/graphql";
import {
  type BenchCompanyUserOption,
  type BenchDriverOption,
  type BenchPartnershipOption,
} from "../bench-run";
import BenchDriverPicker from "./bench-driver-picker";
import BenchUserPicker from "./bench-user-picker";

const styles = {
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
    columnGap: 2,
    rowGap: 3,
    alignItems: "start",
  } satisfies SxProps<Theme>,
};

const getOptionLabel = (option: BenchPartnershipOption) =>
  `${option.companyName} / ${option.contactName}${
    isNil(option.receiverIsaId) ? "" : ` (${option.receiverIsaId})`
  }`;

const isOptionEqualToValue = (
  option: BenchPartnershipOption,
  value: BenchPartnershipOption,
) => option.uuid === value.uuid;

const renderPartnershipInput = (params: AutocompleteRenderInputParams) => (
  <TextField
    {...params}
    helperText="Only partnerships that send to Tenedi."
    label="Customer and partnership"
  />
);

type BenchTargetSectionProps = {
  readonly partnerships: BenchPartnershipOption[];
  readonly partnershipsLoading: boolean;
  readonly partnership: BenchPartnershipOption | null;
  readonly onPartnershipChange: (value: BenchPartnershipOption | null) => void;
  readonly companyActor: BenchCompanyUserOption | null;
  readonly onCompanyActorChange: (value: BenchCompanyUserOption | null) => void;
  readonly testDriver: BenchDriverOption | null;
  readonly onTestDriverChange: (value: BenchDriverOption | null) => void;
};

/**
 * Picks the partnership a run works on, the user of its company the run acts
 * as where a service needs one, and the driver its routes go to.
 */
const BenchTargetSection = ({
  partnerships,
  partnershipsLoading,
  partnership,
  onPartnershipChange,
  companyActor,
  onCompanyActorChange,
  testDriver,
  onTestDriverChange,
}: BenchTargetSectionProps) => {
  const handlePartnershipChange = useCallback(
    (_: unknown, value: BenchPartnershipOption | null) => {
      onPartnershipChange(value);
    },
    [onPartnershipChange],
  );

  return (
    <Box sx={styles.row}>
      <TextField
        disabled
        select
        label="Integration"
        value={BenchIntegrationType.Tenedi}
      >
        <MenuItem value={BenchIntegrationType.Tenedi}>Tenedi EDI</MenuItem>
      </TextField>
      <Autocomplete
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        loading={partnershipsLoading}
        noOptionsText="No partnership sends to Tenedi yet."
        options={partnerships}
        renderInput={renderPartnershipInput}
        value={partnership}
        onChange={handlePartnershipChange}
      />
      <BenchUserPicker
        companyUuid={partnership?.companyUuid ?? null}
        value={companyActor}
        onChange={onCompanyActorChange}
      />
      <BenchDriverPicker
        companyUuid={partnership?.companyUuid ?? null}
        value={testDriver}
        onChange={onTestDriverChange}
      />
    </Box>
  );
};

export default BenchTargetSection;
